import { useState, useEffect, useCallback, useRef } from 'react';

// ============================================
// Types
// ============================================

interface UseAppVisibilityReturn {
  isVisible: boolean;
  wasHidden: boolean;
  resetWasHidden: () => void;
  onVisibilityChange: (callback: (isVisible: boolean) => void) => () => void;
}

// ============================================
// Hook
// ============================================

/**
 * Hook to detect when the app becomes visible/hidden
 * Critical for PWAs on mobile devices where connections can be suspended
 */
export const useAppVisibility = (): UseAppVisibilityReturn => {
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const [wasHidden, setWasHidden] = useState(false);

  // Track if initial mount grace period has passed
  // This prevents false positives from focus/blur events during app startup
  const mountedRef = useRef(false);
  const mountTimeRef = useRef(Date.now());

  useEffect(() => {
    // Give the app 2 seconds to stabilize before tracking visibility changes
    // This prevents false triggers during PWA startup/resume
    const MOUNT_GRACE_PERIOD = 2000;

    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      const timeSinceMount = Date.now() - mountTimeRef.current;

      // Skip events during initial mount grace period
      if (timeSinceMount < MOUNT_GRACE_PERIOD) {
        console.log('[AppVisibility] Ignoring visibility change during grace period');
        setIsVisible(visible);
        return;
      }

      console.log('[AppVisibility] Visibility changed:', visible ? 'visible' : 'hidden');

      if (!visible) {
        setWasHidden(true);
      }

      setIsVisible(visible);
    };

    // Also handle focus/blur for additional reliability on mobile
    const handleFocus = () => {
      const timeSinceMount = Date.now() - mountTimeRef.current;
      if (timeSinceMount < MOUNT_GRACE_PERIOD) {
        console.log('[AppVisibility] Ignoring focus during grace period');
        return;
      }
      console.log('[AppVisibility] Window focused');
      setIsVisible(true);
    };

    const handleBlur = () => {
      const timeSinceMount = Date.now() - mountTimeRef.current;
      if (timeSinceMount < MOUNT_GRACE_PERIOD) {
        console.log('[AppVisibility] Ignoring blur during grace period');
        return;
      }
      console.log('[AppVisibility] Window blurred');
      setWasHidden(true);
      setIsVisible(false);
    };

    // Handle page show event (fired when navigating back via back button)
    const handlePageShow = (event: PageTransitionEvent) => {
      // If page was loaded from bfcache (back/forward cache)
      if (event.persisted) {
        console.log('[AppVisibility] Page restored from bfcache');
        setWasHidden(true); // bfcache restore should trigger reconnection
        setIsVisible(true);
      }
    };

    // Mark as mounted after grace period
    const mountTimer = setTimeout(() => {
      mountedRef.current = true;
      console.log('[AppVisibility] Grace period ended, now tracking visibility');
    }, MOUNT_GRACE_PERIOD);

    // Add all listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      clearTimeout(mountTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Allow components to reset the wasHidden flag after processing
  const resetWasHidden = useCallback(() => {
    console.log('[AppVisibility] Resetting wasHidden flag');
    setWasHidden(false);
  }, []);

  // Allow components to register callbacks for visibility changes
  const onVisibilityChange = useCallback((callback: (isVisible: boolean) => void) => {
    const handler = () => {
      callback(!document.hidden);
    };

    document.addEventListener('visibilitychange', handler);

    return () => {
      document.removeEventListener('visibilitychange', handler);
    };
  }, []);

  return {
    isVisible,
    wasHidden,
    resetWasHidden,
    onVisibilityChange,
  };
};

export default useAppVisibility;
